import { z } from 'zod';
import { PROJECT_ROLE_KEY } from './project-roles';

// Project member status enum
export enum ProjectMemberStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
}

// User schema for project member
export const ProjectMemberUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  emailVerified: z.date().nullable(),
  image: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string().nullable(),
});

// Project member schema
export const ProjectMemberSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  userId: z.string().uuid().nullable().optional(),
  memberId: z.string().uuid().nullable().optional(),
  // Lowercase record-permission role key (e.g. "project_manager"). Validates as
  // any string so org-defined custom keys work — UI surfaces the seeded set via
  // PROJECT_ROLE_KEY.
  roleKey: z.string().min(1).max(100).default(PROJECT_ROLE_KEY.CONTRIBUTOR),
  // Deprecated alias for `roleKey`. Retained on the response shape so older
  // clients that read `.role` keep working — new code should use roleKey.
  role: z.string().min(1).max(100).optional(),
  status: z.nativeEnum(ProjectMemberStatusEnum).default(ProjectMemberStatusEnum.ACTIVE),
  settings: z.record(z.any()).default({}),
  /**
   * Billing inheritance flag. When true (default), the member's billable job
   * roles are inherited from the parent entity's EntityMemberJobRole roster.
   * When false, the member uses the ProjectMemberJobRole rows as an explicit
   * per-project override (empty = access only, no billing).
   */
  billingInheritsFromEntity: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
  user: ProjectMemberUserSchema.nullable().optional(),
});

export type ProjectMember = z.infer<typeof ProjectMemberSchema>;

// Create project member schema
export const CreateProjectMemberSchema = ProjectMemberSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  settings: true,
  user: true,
}).extend({
  settings: z.record(z.any()).optional(),
});

export type CreateProjectMember = z.infer<typeof CreateProjectMemberSchema>;

// Update project member schema
export const UpdateProjectMemberSchema = CreateProjectMemberSchema.partial();

export type UpdateProjectMember = z.infer<typeof UpdateProjectMemberSchema>;

// Project member response schema
export const ProjectMemberResponseSchema = ProjectMemberSchema;

export type ProjectMemberResponse = z.infer<typeof ProjectMemberResponseSchema>;

// Project members response schema
export const ProjectMembersResponseSchema = z.object({
  data: z.array(ProjectMemberResponseSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
  }),
});

export type ProjectMembersResponse = z.infer<typeof ProjectMembersResponseSchema>;
